// CampaignAlertModal.tsx
import React from "react";
import { Modal, Button, Divider, Row, Col } from "antd";
import {
  CloseCircleFilled,
  InfoCircleFilled,
  PauseCircleFilled,
  PlayCircleFilled,
  ScheduleFilled,
} from "@ant-design/icons";
import { Variant } from "../../constants/type";

type Props = {
  open: boolean;
  variant: Variant;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLoading?: boolean;
};

const CFG: Record<
  Variant,
  {
    title: string;
    description: string;
    icon: React.ReactNode;
    confirmText: string;
    cancelText: string;
  }
> = {
  cancel: {
    title: "Cancel Campaign?",
    description:
      "Are you sure you want to cancel this campaign? This action cannot be undone.",
    icon: <CloseCircleFilled style={{ fontSize: 80, color: "#ef5350" }} />,
    confirmText: "Cancel Campaign",
    cancelText: "Go Back",
  },
  pause: {
    title: "Pause Campaign?",
    description:
      "Pausing will stop all future deliveries until you resume this campaign. Do you want to continue?",
    icon: <PauseCircleFilled style={{ fontSize: 80, color: "#fbc02d" }} />,
    confirmText: "Pause Campaign",
    cancelText: "Go Back",
  },
  stop: {
    title: "Stop Campaign?",
    description:
      "This will permanently stop the campaign and cannot be reversed. Do you want to proceed?",
    icon: <i className="icon-stop-red" />,
    confirmText: "Stop Campaign",
    cancelText: "Go Back",
  },
  resume: {
    title: "Resume Campaign?",
    description:
      "Resume this campaign and continue with the scheduled deliveries.",
    icon: <PlayCircleFilled style={{ fontSize: 80, color: "#43a047" }} />,
    confirmText: "Resume Campaign",
    cancelText: "Cancel",
  },
  delete: {
    title: "Delete Campaign?",
    description:
      "This campaign will be permanently deleted. This action cannot be undone.",
    icon: <i className="icon-trash-red" />,
    confirmText: "Delete Campaign",
    cancelText: "Cancel",
  },
  activate: {
    title: "Activate Campaign?",
    description:
      "The campaign will be scheduled to send immediately. If the send time has not started yet, you can still edit or cancel it. Do you want to continue?",
    icon: <InfoCircleFilled style={{ fontSize: 80, color: "#1565C0" }} />,
    confirmText: "Continue",
    cancelText: "Cancel",
  },
};

export default function CampaignAlertModal({
  open,
  variant,
  onCancel,
  onConfirm,
  confirmLoading,
}: Props) {
  const cfg = CFG[variant];

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={560}
      destroyOnClose
      closable={false}
    >
      <div
        style={{
          display: "grid",
          placeItems: "center",
          gap: 12,
          paddingTop: 8,
        }}
      >
        {cfg.icon}
        <h3 style={{ margin: 0, fontSize: 20 }}>{cfg.title}</h3>
        <p style={{ margin: 0, color: "#6b7280", textAlign: "center" }}>
          {cfg.description}
        </p>
      </div>
      <Divider />
      <Row gutter={[16, 16]}>
        <Col xs={12} md={12} lg={12}>
          {" "}
          <Button size="large" style={{ width: "100%" }} onClick={onCancel}>
            {cfg.cancelText}
          </Button>
        </Col>
        <Col xs={12} md={12} lg={12}>
          <Button
            size="large"
            type="primary"
            onClick={onConfirm}
            loading={confirmLoading}
            style={{ width: "100%" }}
          >
            {cfg.confirmText}
          </Button>
        </Col>
      </Row>
    </Modal>
  );
}
